import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getHomeTabs,
  deleteHomeTab,
  updateHomeTab,
  reorderHomeTabs,
} from "@/services/firebase/homeTab";
import { HomeTab } from "@/types/homeTab";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import ActionMenu from "@/components/common/ActionMenu";
import { useDebounce } from "@/hooks/useDebounce";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LayoutGrid, ChevronUp, ChevronDown } from "lucide-react";
import {
  AdminPage,
  AdminPageHeader,
  AdminSearchInput,
  AdminLoadingSkeleton,
  AdminErrorAlert,
  AdminEmptyState,
  AdminDataTable,
} from "@/components/admin-ui";
import { AdminTableColumn } from "@/components/admin-ui/admin-data-table";

const columns: AdminTableColumn[] = [
  { key: "order", label: "順序", sortable: true },
  { key: "tabId", label: "Tab ID" },
  { key: "title", label: "標題" },
  { key: "type", label: "類型", hideBelow: "md" },
  { key: "status", label: "狀態" },
  { key: "actions", label: "操作" },
];

const getTypeLabel = (type: string) => {
  const labels: Record<string, string> = {
    featured: "精選",
    ranking: "排行榜",
    tag_filter: "標籤篩選",
    custom: "自訂列表",
  };
  return labels[type] || type;
};

export default function HomeLayoutPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    id: string | null;
    title: string;
  }>({ isOpen: false, id: null, title: "" });
  const [reorderError, setReorderError] = useState<string | null>(null);

  const { data: tabs, isLoading, error } = useQuery({
    queryKey: ["homeTabs"],
    queryFn: () => getHomeTabs(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteHomeTab(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["homeTabs"] });
      setDeleteConfirm({ isOpen: false, id: null, title: "" });
      setDeleteError(null);
    },
    onError: (error) => {
      console.error("刪除 Tab 失敗:", error);
      setDeleteError(error instanceof Error ? error.message : "未知錯誤");
      setDeleteConfirm({ isOpen: false, id: null, title: "" });
    },
  });

  const [toggleError, setToggleError] = useState<string | null>(null);

  const toggleEnabledMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      updateHomeTab(id, { enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["homeTabs"] });
      setToggleError(null);
    },
    onError: (error) => {
      console.error("更新 Tab 狀態失敗:", error);
      setToggleError(error instanceof Error ? error.message : "更新狀態失敗");
      setTimeout(() => setToggleError(null), 3000);
    },
  });

  const moveMutation = useMutation({
    mutationFn: (updates: Array<{ id: string; order: number }>) =>
      reorderHomeTabs(updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["homeTabs"] });
      setReorderError(null);
    },
    onError: (error) => {
      console.error("調整順序失敗:", error);
      setReorderError(error instanceof Error ? error.message : "未知錯誤");
    },
  });

  // 過濾和搜尋
  const filteredTabs = useMemo(() => {
    if (!tabs || tabs.length === 0) return [];

    return tabs.filter((tab) => {
      const searchLower = debouncedSearchTerm.toLowerCase();
      const matchesSearch =
        !searchLower ||
        tab.id.toLowerCase().includes(searchLower) ||
        tab.title.toLowerCase().includes(searchLower) ||
        (tab.icon && tab.icon.includes(searchLower));

      return matchesSearch;
    });
  }, [tabs, debouncedSearchTerm]);

  const handleDelete = (id: string, title: string) => {
    setDeleteConfirm({ isOpen: true, id, title });
  };

  const confirmDelete = () => {
    if (deleteConfirm.id) {
      deleteMutation.mutate(deleteConfirm.id);
    }
  };

  const handleToggleEnabled = (tab: HomeTab) => {
    toggleEnabledMutation.mutate({ id: tab.id, enabled: !tab.enabled });
  };

  const handleMoveUp = (tab: HomeTab) => {
    if (!tabs) return;
    const currentIndex = tabs.findIndex((t) => t.id === tab.id);
    if (currentIndex <= 0) return;

    const updates = tabs.map((t, index) => {
      if (index === currentIndex) {
        return { id: t.id, order: tabs[currentIndex - 1].order };
      } else if (index === currentIndex - 1) {
        return { id: t.id, order: tab.order };
      }
      return { id: t.id, order: t.order };
    });
    moveMutation.mutate(updates);
  };

  const handleMoveDown = (tab: HomeTab) => {
    if (!tabs) return;
    const currentIndex = tabs.findIndex((t) => t.id === tab.id);
    if (currentIndex >= tabs.length - 1) return;

    const updates = tabs.map((t, index) => {
      if (index === currentIndex) {
        return { id: t.id, order: tabs[currentIndex + 1].order };
      } else if (index === currentIndex + 1) {
        return { id: t.id, order: tab.order };
      }
      return { id: t.id, order: t.order };
    });
    moveMutation.mutate(updates);
  };

  if (isLoading) {
    return (
      <AdminPage>
        <AdminPageHeader title="首頁佈局管理" />
        <AdminLoadingSkeleton />
      </AdminPage>
    );
  }

  if (error) {
    return (
      <AdminPage>
        <AdminPageHeader title="首頁佈局管理" />
        <AdminErrorAlert
          message={`載入 Tab 時發生錯誤：${error instanceof Error ? error.message : "未知錯誤"}`}
        />
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <AdminPageHeader
        title="首頁佈局管理"
        createHref="/home-layout/new"
        createLabel="新增 Tab"
      />

      {/* 錯誤訊息 */}
      {deleteError && (
        <AdminErrorAlert
          message={`刪除 Tab 失敗：${deleteError}`}
          onDismiss={() => setDeleteError(null)}
        />
      )}
      {reorderError && (
        <AdminErrorAlert
          message={`調整順序失敗：${reorderError}`}
          onDismiss={() => setReorderError(null)}
        />
      )}
      {toggleError && (
        <AdminErrorAlert
          message={`更新狀態失敗：${toggleError}`}
          onDismiss={() => setToggleError(null)}
        />
      )}

      {/* 搜尋欄 */}
      <div className="mb-6">
        <AdminSearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="搜尋 Tab ID、標題或圖示..."
          className="max-w-md"
        />
      </div>

      {/* Tab 列表 */}
      <AdminDataTable<HomeTab>
        columns={columns}
        data={filteredTabs}
        emptyIcon={<LayoutGrid className="h-5 w-5" />}
        emptyTitle={searchTerm ? "沒有符合條件的 Tab" : "尚無 Tab 資料"}
        emptyActionHref={!searchTerm ? "/home-layout/new" : undefined}
        emptyActionLabel="新增第一個 Tab"
        getRowId={(item) => item.id}
        renderCell={(tab, col) => {
          const tabIndex = filteredTabs.findIndex((t) => t.id === tab.id);

          switch (col.key) {
            case "order":
              return (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">
                    {tab.order}
                  </span>
                  <div className="flex flex-col gap-0.5">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveUp(tab);
                      }}
                      disabled={tabIndex === 0 || moveMutation.isPending}
                      className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
                      title="上移"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveDown(tab);
                      }}
                      disabled={
                        tabIndex === filteredTabs.length - 1 ||
                        moveMutation.isPending
                      }
                      className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
                      title="下移"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            case "tabId":
              return (
                <span className="text-sm font-medium text-foreground font-mono">
                  {tab.id}
                </span>
              );
            case "title":
              return (
                <span
                  className={`text-sm font-semibold text-foreground ${
                    !tab.enabled ? "opacity-60" : ""
                  }`}
                >
                  {tab.title}
                </span>
              );
            case "type":
              return (
                <span className="text-muted-foreground">
                  {getTypeLabel(tab.type)}
                </span>
              );
            case "status":
              return (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleEnabled(tab);
                  }}
                  disabled={toggleEnabledMutation.isPending}
                >
                  <Badge variant={tab.enabled ? "success" : "secondary"}>
                    {tab.enabled ? "啟用" : "停用"}
                  </Badge>
                </button>
              );
            case "actions":
              return (
                <ActionMenu
                  actions={[
                    {
                      label: "編輯",
                      onClick: () => navigate(`/home-layout/${tab.id}/edit`),
                    },
                    {
                      label: "刪除",
                      onClick: () => handleDelete(tab.id, tab.title),
                      disabled: deleteMutation.isPending,
                      variant: "danger",
                    },
                  ]}
                />
              );
            default:
              return null;
          }
        }}
      />

      {/* 刪除確認對話框 */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="確認刪除"
        message={`確定要刪除 Tab「${deleteConfirm.title}」嗎？此操作無法復原。`}
        confirmText="刪除"
        cancelText="取消"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null, title: "" })}
      />
    </AdminPage>
  );
}
